import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Alert, Box, IconButton, Typography } from '@mui/material';

import { SexOffenderApi } from '@/api';
import { ISexOffender, IUser, RiskLevelLabels } from '@/models';

interface SexOffenderBannerProps {
  personId: string;
}

export const SexOffenderBanner = ({ personId }: SexOffenderBannerProps) => {
  const navigate = useNavigate();
  const [offender, setOffender] = useState<ISexOffender | null>(null);

  useEffect(() => {
    if (!personId) return;

    SexOffenderApi.getByPersonId(personId)
      .then(({ data }) => setOffender(data))
      .catch(() => {
        // 404 means no record — that's fine
      });
  }, [personId]);

  if (!offender) return null;

  const officer = offender.assignedOfficerId as IUser | undefined;
  const officerName = officer ? officer.fullName || officer.email : '-';
  const offenderUrl = `/tools/sex-offenders/${offender._id}`;

  return (
    <Alert
      icon={<InfoOutlinedIcon fontSize="small" />}
      severity="error"
      sx={{
        mx: 2,
        mb: 1,
        mt: 2,
        border: '1px solid #EE3E5C',
        '& .MuiAlert-icon': {
          color: '#EE3E5C',
          alignSelf: 'flex-start',
          mt: '2px',
        },
        '& .MuiAlert-action': {
          alignSelf: 'center',
          mt: -1,
        },
      }}
      action={
        <IconButton
          size="small"
          onClick={() => navigate(offenderUrl)}
          title="View sex offender record"
          sx={{ color: '#3E5A6A' }}
        >
          <OpenInNewIcon fontSize="small" />
        </IconButton>
      }
    >
      <Box display="flex" flexDirection="column" gap={0.5}>
        <Typography variant="body2" fontWeight={500} fontSize={16}>
          Registered sex offender
        </Typography>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Typography variant="body2" color="inherit">
            <Box component="span" fontWeight={500}>
              ID:
            </Box>{' '}
            {offender.sexOffenderId}
          </Typography>
          <Typography variant="body2" color="inherit">
            <Box component="span" fontWeight={500}>
              Officer:
            </Box>{' '}
            {officerName}
          </Typography>
          <Typography variant="body2" color="inherit">
            <Box component="span" fontWeight={500}>
              Risk level:
            </Box>{' '}
            {RiskLevelLabels[offender.riskLevel] ?? '-'}
          </Typography>
        </Box>
      </Box>
    </Alert>
  );
};
